import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User, LogIn } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({});
  const [logoType, setLogoType] = useState('kolej');
  const [remainingAttempts, setRemainingAttempts] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockedUntil, setBlockedUntil] = useState(null);
  const [remainingMinutes, setRemainingMinutes] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const savedLogoType = localStorage.getItem('siteLogo') || 'kolej';
    setLogoType(savedLogoType);
    fetchSettings();
    checkBlockStatus();
  }, []);

  const checkBlockStatus = async () => {
    try {
      const response = await fetch('http://localhost/api/auth.php?action=check_block', {
        method: 'GET',
        credentials: 'include',
      });
      
      if (response.ok) {
        const text = await response.text();
        const data = text ? JSON.parse(text) : {};
        if (data.blocked) {
          setIsBlocked(true);
          if (data.blocked_until) {
            const blockTime = new Date(data.blocked_until * 1000);
            setBlockedUntil(blockTime);
            setRemainingMinutes(data.remaining_minutes || 5);
          }
        } else if (data.attempts) {
          setRemainingAttempts(data.remaining_attempts || null);
          setIsBlocked(false);
          setBlockedUntil(null);
          setRemainingMinutes(null);
        }
      }
    } catch (error) {
      // Hata durumunda sessizce devam et
    }
  };

  useEffect(() => {
    if (Object.keys(settings).length > 0) {
      updateFavicon();
    }
  }, [settings, logoType]);

  // Bloklama süresini gerçek zamanlı olarak güncelle
  useEffect(() => {
    if (isBlocked && blockedUntil) {
      const interval = setInterval(() => {
        const now = new Date();
        const diff = blockedUntil.getTime() - now.getTime();
        
        if (diff > 0) {
          const minutes = Math.ceil(diff / (1000 * 60));
          setRemainingMinutes(minutes);
        } else {
          // Bloklama süresi doldu
          setIsBlocked(false);
          setBlockedUntil(null);
          setRemainingMinutes(null);
          setRemainingAttempts(null);
          clearInterval(interval);
        }
      }, 1000); // Her saniye güncelle

      return () => clearInterval(interval);
    }
  }, [isBlocked, blockedUntil]);

  const updateFavicon = () => {
    const faviconLink = document.querySelector("link[rel~='icon']") || document.createElement('link');
    faviconLink.rel = 'icon';
    const faviconUrl = logoType === 'kurs-merkezi'
      ? (settings.kurs_merkezi_favicon || '/favicon.ico')
      : (settings.kolej_favicon || '/favicon.ico');
    faviconLink.href = faviconUrl;
    if (!document.querySelector("link[rel~='icon']")) {
      document.getElementsByTagName('head')[0].appendChild(faviconLink);
    } else {
      // Mevcut favicon link'ini güncelle
      const existingLink = document.querySelector("link[rel~='icon']");
      if (existingLink) {
        existingLink.href = faviconUrl;
      }
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch('http://localhost/api/settings.php', {
        credentials: 'include',
      });
      if (response.ok) {
        const text = await response.text();
        const data = text ? JSON.parse(text) : {};
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const logoSrc = logoType === 'kurs-merkezi'
    ? (settings.kurs_merkezi_logo || '/kursmerkezi.png')
    : (settings.kolej_logo || '/logo.png');
  const logoAlt = logoType === 'kurs-merkezi' ? 'Kurs Merkezi' : 'Serpil Şahin Koleji';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error('Lütfen kullanıcı adı ve şifre girin');
      return;
    }

    setLoading(true);

    let response;
    let status = 0;
    let isOk = false;
    let data = {};
    
    try {
      response = await fetch('http://localhost/api/auth.php?action=login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      });

      // Response'u okumadan önce tüm bilgileri al (body okunmadan önce)
      status = response.status;
      isOk = response.ok;

      // Response body'yi okumayı dene
      let text = '';
      try {
        // Body'yi hemen oku - response.text() çağrısından sonra response'a erişme
        text = await response.text();
        
        // JSON parse et
        if (text) {
          try {
            data = JSON.parse(text);
          } catch (parseError) {
            console.error('JSON parse error:', parseError);
            console.error('Response text:', text);
            // JSON parse hatası olsa bile error mesajını göster
            data = { error: text || 'Sunucu yanıtı okunamadı' };
          }
        }
      } catch (readError) {
        console.error('Response read error:', readError);
        // Response body okunamadı, check_block endpoint'inden bilgi al
        try {
          const blockCheck = await fetch('http://localhost/api/auth.php?action=check_block', {
            method: 'GET',
            credentials: 'include',
          });
          if (blockCheck.ok) {
            const blockText = await blockCheck.text();
            const blockData = blockText ? JSON.parse(blockText) : {};
            
            if (blockData.blocked) {
              data = { error: 'Çok fazla başarısız giriş denemesi. Lütfen ' + (blockData.remaining_minutes || 5) + ' dakika sonra tekrar deneyin.' };
            } else if (blockData.remaining_attempts !== undefined) {
              const remaining = blockData.remaining_attempts;
              data = { error: 'Kullanıcı adı veya şifre hatalı. Kalan deneme hakkı: ' + remaining };
            } else {
              // Status code'a göre varsayılan mesaj oluştur
              if (status === 401) {
                data = { error: 'Kullanıcı adı veya şifre hatalı. Lütfen tekrar deneyin.' };
              } else if (status === 429) {
                data = { error: 'Çok fazla başarısız giriş denemesi. Lütfen 5 dakika sonra tekrar deneyin.' };
              } else {
                data = { error: 'Sunucu yanıtı okunamadı: ' + readError.message };
              }
            }
          } else {
            // check_block da başarısız, status code'a göre varsayılan mesaj
            if (status === 401) {
              data = { error: 'Kullanıcı adı veya şifre hatalı. Lütfen tekrar deneyin.' };
            } else if (status === 429) {
              data = { error: 'Çok fazla başarısız giriş denemesi. Lütfen 5 dakika sonra tekrar deneyin.' };
            } else {
              data = { error: 'Sunucu yanıtı okunamadı: ' + readError.message };
            }
          }
        } catch (checkError) {
          console.error('Block check error:', checkError);
          // check_block da başarısız, status code'a göre varsayılan mesaj
          if (status === 401) {
            data = { error: 'Kullanıcı adı veya şifre hatalı. Lütfen tekrar deneyin.' };
          } else if (status === 429) {
            data = { error: 'Çok fazla başarısız giriş denemesi. Lütfen 5 dakika sonra tekrar deneyin.' };
          } else {
            data = { error: 'Sunucu yanıtı okunamadı: ' + readError.message };
          }
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // rrweb-recorder gibi araçların clone hatasını yakala
      if (error.message && error.message.includes('clone')) {
        // Clone hatası oluşmuş ama fetch başarılı olmuş olabilir
        // check_block endpoint'inden bilgi al
        try {
          const blockCheck = await fetch('http://localhost/api/auth.php?action=check_block', {
            method: 'GET',
            credentials: 'include',
          });
          if (blockCheck.ok) {
            const blockText = await blockCheck.text();
            const blockData = blockText ? JSON.parse(blockText) : {};
            
            if (blockData.blocked) {
              data = { error: 'Çok fazla başarısız giriş denemesi. Lütfen ' + (blockData.remaining_minutes || 5) + ' dakika sonra tekrar deneyin.' };
              setIsBlocked(true);
              setRemainingMinutes(blockData.remaining_minutes || 5);
              setBlockedUntil(new Date(Date.now() + (blockData.remaining_minutes || 5) * 60 * 1000));
            } else if (blockData.remaining_attempts !== undefined) {
              const remaining = blockData.remaining_attempts;
              data = { error: 'Kullanıcı adı veya şifre hatalı. Kalan deneme hakkı: ' + remaining };
              setRemainingAttempts(remaining);
              setIsBlocked(false);
            } else {
              // Status code'a göre varsayılan mesaj
              if (status === 401) {
                data = { error: 'Kullanıcı adı veya şifre hatalı. Lütfen tekrar deneyin.' };
              } else if (status === 429) {
                data = { error: 'Çok fazla başarısız giriş denemesi. Lütfen 5 dakika sonra tekrar deneyin.' };
                setIsBlocked(true);
                setRemainingMinutes(5);
                setBlockedUntil(new Date(Date.now() + 5 * 60 * 1000));
              } else {
                data = { error: 'Giriş başarısız. Lütfen tekrar deneyin.' };
              }
            }
          } else {
            // check_block başarısız, status code'a göre varsayılan mesaj
            if (status === 401) {
              data = { error: 'Kullanıcı adı veya şifre hatalı. Lütfen tekrar deneyin.' };
            } else if (status === 429) {
              data = { error: 'Çok fazla başarısız giriş denemesi. Lütfen 5 dakika sonra tekrar deneyin.' };
              setIsBlocked(true);
              setRemainingMinutes(5);
              setBlockedUntil(new Date(Date.now() + 5 * 60 * 1000));
            } else {
              data = { error: 'Giriş başarısız. Lütfen tekrar deneyin.' };
            }
          }
        } catch (checkError) {
          console.error('Block check error:', checkError);
          // check_block da başarısız, status code'a göre varsayılan mesaj
          if (status === 401) {
            data = { error: 'Kullanıcı adı veya şifre hatalı. Lütfen tekrar deneyin.' };
          } else if (status === 429) {
            data = { error: 'Çok fazla başarısız giriş denemesi. Lütfen 5 dakika sonra tekrar deneyin.' };
            setIsBlocked(true);
            setRemainingMinutes(5);
            setBlockedUntil(new Date(Date.now() + 5 * 60 * 1000));
          } else {
            data = { error: 'Giriş başarısız. Lütfen tekrar deneyin.' };
          }
        }
      } else if (error.name === 'TypeError' && error.message.includes('Failed to execute')) {
        // Response ile ilgili genel hata
        toast.error('Giriş hatası: Sunucu yanıtı işlenemedi. Lütfen tekrar deneyin.');
        setLoading(false);
        return;
      } else {
        toast.error('Bağlantı hatası: ' + (error.message || 'Bilinmeyen hata'));
        setLoading(false);
        return;
      }
    }

    // Başarılı giriş
    if (isOk && data.success) {
      setRemainingAttempts(null);
      setIsBlocked(false);
      setBlockedUntil(null);
      setRemainingMinutes(null);
      toast.success('Giriş başarılı');
      navigate('/admin/dashboard');
      return;
    }
    
    // Bloklama durumu (429 Too Many Requests)
    if (status === 429) {
      setIsBlocked(true);
      const errorMessage = data.error || 'Çok fazla başarısız giriş denemesi';
      toast.error(errorMessage, { duration: 6000 });
      
      // Bloklama süresini parse et (eğer mesajda dakika varsa)
      const minutesMatch = errorMessage.match(/(\d+)\s*dakika/);
      if (minutesMatch) {
        const minutes = parseInt(minutesMatch[1]);
        setRemainingMinutes(minutes);
        const blockEndTime = new Date(Date.now() + minutes * 60 * 1000);
        setBlockedUntil(blockEndTime);
      } else {
        // Varsayılan 5 dakika
        setRemainingMinutes(5);
        const blockEndTime = new Date(Date.now() + 5 * 60 * 1000);
        setBlockedUntil(blockEndTime);
      }
      return;
    }
    
    // Normal hata (401 Unauthorized veya diğer)
    const errorMessage = data.error || 'Giriş başarısız';
    
    // Kalan deneme hakkını parse et
    const attemptsMatch = errorMessage.match(/Kalan deneme hakkı:\s*(\d+)/);
    if (attemptsMatch) {
      const remaining = parseInt(attemptsMatch[1]);
      setRemainingAttempts(remaining);
      setIsBlocked(false);
      toast.error(errorMessage, { duration: 5000 });
    } else {
      // Status 401 ise varsayılan olarak bir uyarı göster
      if (status === 401) {
        // Kalan deneme hakkı bilgisi yok, varsayılan mesaj göster
        setRemainingAttempts(null);
        toast.error(errorMessage);
      } else {
        setRemainingAttempts(null);
        toast.error(errorMessage);
      }
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-red-900/10 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <img 
                src={logoSrc}
                alt={logoAlt}
                className="h-20 w-auto object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const fallback = e.target.nextElementSibling;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
              <div className="hidden w-20 h-20 rounded-full bg-gradient-to-br from-red-900 to-red-800 flex items-center justify-center" style={{display: 'none'}}>
                <Lock className="text-white" size={40} />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Yönetici Girişi</h1>
            <p className="text-gray-600">Yönetici paneline erişmek için giriş yapın</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Kullanıcı Adı veya E-posta
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Kullanıcı adı veya e-posta"
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Şifre
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Şifre"
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || isBlocked}
              className="w-full bg-gradient-to-r from-red-900 to-red-800 hover:from-red-800 hover:to-red-700 text-white font-semibold py-3 rounded-lg shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Giriş yapılıyor...
                </>
              ) : isBlocked ? (
                <>
                  <Lock size={20} />
                  Giriş Bloklandı
                </>
              ) : (
                <>
                  <LogIn size={20} />
                  Giriş Yap
                </>
              )}
            </Button>
            
            {/* Uyarı Mesajları */}
            {remainingAttempts !== null && remainingAttempts < 5 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                <p className="font-semibold">Uyarı:</p>
                <p>Kalan deneme hakkı: <span className="font-bold">{remainingAttempts}</span></p>
              </div>
            )}
            
            {isBlocked && remainingMinutes !== null && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                <p className="font-semibold mb-1">⚠️ Giriş Geçici Olarak Bloklandı</p>
                <p>Çok fazla başarısız giriş denemesi yapıldı.</p>
                <p className="mt-1 font-bold">
                  Lütfen <span className="text-red-600">{remainingMinutes}</span> dakika sonra tekrar deneyin.
                </p>
              </div>
            )}
          </form>

          <div className="mt-6 text-center">
            <a
              href="/kolej"
              className="text-sm text-gray-600 hover:text-[#C0A062] transition-colors"
            >
              Ana Sayfaya Dön
            </a>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminLogin;

